import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Box,
  FormHelperText,
  CircularProgress,
  Typography,
} from '@mui/material';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Address, Country, Region } from '../../../../client/services';
import { useAppSelector } from '../../../../store';
import { extractCoordinatesFromGoogleMapsUrl } from '../../../../utils/googleMapsUrlParser';
import { customIcon } from '../../../../utils/icon';
import { toastHandler } from '../../../../utils/toastHandler';

interface AddressCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (address: any) => void;
  customerId: string;
  /** Selected customer phone — auto-sent on create (API requires phoneNumber) */
  customerCountryCallingCode?: string | null;
  customerMobileNumber?: string | null;
}

const buildCustomerPhoneNumber = (
  countryCallingCode?: string | null,
  mobileNumber?: string | null
): string => {
  const code = String(countryCallingCode || '').trim();
  const mobile = String(mobileNumber || '').trim();
  if (!mobile) return '';
  if (!code) return mobile;
  // Avoid doubling code if mobile already includes it
  const digitsCode = code.replace(/\D/g, '');
  const digitsMobile = mobile.replace(/\D/g, '');
  if (digitsCode && digitsMobile.startsWith(digitsCode)) {
    return mobile.startsWith('+') ? mobile : `+${digitsMobile}`;
  }
  const normalizedCode = code.startsWith('+') ? code : `+${digitsCode || code}`;
  return `${normalizedCode}${digitsMobile || mobile}`;
};

const MapClickHandler: React.FC<{
  onLocationSelect: (lat: number, lng: number) => void;
}> = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const AddressLocationMap: React.FC<{
  latitude: number;
  longitude: number;
  onLocationSelect: (lat: number, lng: number) => void;
}> = React.memo(({ latitude, longitude, onLocationSelect }) => (
  <Box
    sx={{
      height: 300,
      width: '100%',
      border: '1px solid #ddd',
      borderRadius: 1,
      overflow: 'hidden',
      position: 'relative',
      zIndex: 0,
      '& .leaflet-container': {
        height: '100%',
        width: '100%',
        zIndex: 0,
      },
    }}
  >
    <MapContainer
      center={[latitude, longitude]}
      zoom={13}
      style={{ height: '100%', width: '100%', zIndex: 0 }}
      attributionControl={false}
      keyboard={false}
    >
      <TileLayer url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' />
      <MapClickHandler onLocationSelect={onLocationSelect} />
      <Marker
        position={[latitude, longitude]}
        icon={customIcon}
      />
    </MapContainer>
  </Box>
));

const AddressCreateDialog: React.FC<AddressCreateDialogProps> = ({
  open,
  onClose,
  onSuccess,
  customerId,
  customerCountryCallingCode,
  customerMobileNumber,
}) => {
  const userRole = useAppSelector((state) => state.auth.user.userRole);
  const [addressType, setAddressType] = useState<string>('home');
  const [addressName, setAddressName] = useState<string>('');
  const [buildingNumber, setBuildingNumber] = useState<string>('');
  const [zone, setZone] = useState<string>('');
  const [street, setStreet] = useState<string>('');
  const [latitude, setLatitude] = useState<number | null>(25.3548); // Default to Doha, Qatar
  const [longitude, setLongitude] = useState<number | null>(51.1839); // Default to Doha, Qatar
  const [isDefault, setIsDefault] = useState<boolean>(false);
  const [countryId, setCountryId] = useState<string>('');
  const [regionId, setRegionId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isFetchingDefaults, setIsFetchingDefaults] = useState(true);
  const [googleMapsUrl, setGoogleMapsUrl] = useState<string>('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const customerPhoneNumber = buildCustomerPhoneNumber(
    customerCountryCallingCode,
    customerMobileNumber
  );

  // Fetch default country and region
  useEffect(() => {
    const fetchDefaults = async () => {
      setIsFetchingDefaults(true);
      try {
        // Fetch all countries and regions, then filter by code (QA for Qatar)
        // Backend only allows search, limit, skip - not code filter
        const countries = await Country.find({
          query: { limit: 100 },
        });
        const regions = await Region.find({
          query: { limit: 100 },
        });

        // Filter by code on frontend
        const qatarCountry = countries?.data?.find((c: any) => c.code === 'QA');
        const qatarRegion = regions?.data?.find((r: any) => r.code === 'QA');

        if (qatarCountry) {
          setCountryId(qatarCountry.id);
        }
        if (qatarRegion) {
          setRegionId(qatarRegion.id);
        }
      } catch (error) {
        console.error('Error fetching default country/region:', error);
      } finally {
        setIsFetchingDefaults(false);
      }
    };

    if (open) {
      fetchDefaults();
    }
  }, [open]);

  const handleMapClick = useCallback((lat: number, lng: number) => {
    setLatitude(lat);
    setLongitude(lng);
    setErrors((prev) => {
      if (!prev.latitude && !prev.longitude) return prev;
      const newErrors = { ...prev };
      delete newErrors.latitude;
      delete newErrors.longitude;
      return newErrors;
    });
  }, []);

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const url = event.target.value;
    setGoogleMapsUrl(url);
    setUrlError(null);
  };

  const handleExtractFromUrl = async () => {
    if (!googleMapsUrl.trim()) {
      setUrlError('Please enter a Google Maps URL');
      return;
    }

    setIsExtracting(true);
    setUrlError(null);

    try {
      const result = await extractCoordinatesFromGoogleMapsUrl(googleMapsUrl);
      
      if (result.success && result.latitude !== null && result.longitude !== null) {
        setLatitude(result.latitude);
        setLongitude(result.longitude);
        setUrlError(null);
        
        // Clear coordinate errors
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.latitude;
          delete newErrors.longitude;
          return newErrors;
        });
        
        const successMessage = `✅ Coordinates extracted: ${result.latitude}, ${result.longitude}`;
        toastHandler({ type: 'success', message: successMessage });
        
        // Clear the URL input after successful extraction
        setGoogleMapsUrl('');
      } else {
        const errorMessage = result.error || 'Failed to extract coordinates';
        setUrlError(errorMessage);
        toastHandler({ type: 'error', message: `❌ ${errorMessage}` });
      }
    } catch (error) {
      const errorMessage = 'Error processing URL';
      setUrlError(errorMessage);
      toastHandler({ type: 'error', message: `❌ ${errorMessage}` });
    } finally {
      setIsExtracting(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setAddressType('home');
    setAddressName('');
    setBuildingNumber('');
    setZone('');
    setStreet('');
    setLatitude(25.3548);
    setLongitude(51.1839);
    setIsDefault(false);
    setGoogleMapsUrl('');
    setUrlError(null);
    setErrors({});
    onClose();
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!addressName.trim()) {
      newErrors.addressName = 'Address name is required';
    }
    if (!buildingNumber.trim()) {
      newErrors.buildingNumber = 'Building number is required';
    }
    if (!zone.trim()) {
      newErrors.zone = 'Zone is required';
    }
    if (!street.trim()) {
      newErrors.street = 'Street is required';
    }
    if (latitude === null || latitude < -90 || latitude > 90) {
      newErrors.latitude = 'Valid latitude is required (-90 to 90)';
    }
    if (longitude === null || longitude < -180 || longitude > 180) {
      newErrors.longitude = 'Valid longitude is required (-180 to 180)';
    }
    if (!countryId) {
      newErrors.countryId = 'Country is required';
    }
    if (!regionId) {
      newErrors.regionId = 'Region is required';
    }
    if (!customerPhoneNumber) {
      newErrors.phoneNumber =
        'Selected customer has no phone number — cannot create address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    setIsLoading(true);
    try {
      const addressData: any = {
        addressType,
        addressName: addressName.trim(),
        buildingNumber: buildingNumber.trim(),
        zone: zone.trim(),
        street: street.trim(),
        latitude: latitude !== null ? Number(latitude) : null,
        longitude: longitude !== null ? Number(longitude) : null,
        isDefault,
        countryId,
        regionId,
        // Required by address API — taken from selected order customer
        phoneNumber: customerPhoneNumber,
        phoneContactType: 'whatsapp',
      };

      // If admin/orderManager, include userId to create address for customer
      if (userRole === 'admin' || userRole === 'orderManager') {
        addressData.userId = customerId;
      }

      const createdAddress = await Address.create(addressData);
      onSuccess(createdAddress);
      handleClose();
    } catch (error: any) {
      console.error('Error creating address:', error);
      setErrors({
        submit: error?.message || 'Failed to create address. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      scroll='paper'
      disableEnforceFocus
    >
      <DialogTitle>Add New Address</DialogTitle>
      <DialogContent>
        {isFetchingDefaults ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {/* Address Type */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Address Type</InputLabel>
                  <Select
                    value={addressType}
                    onChange={(e) => setAddressType(e.target.value)}
                    label='Address Type'
                  >
                    <MenuItem value='home'>Home</MenuItem>
                    <MenuItem value='work'>Work</MenuItem>
                    <MenuItem value='other'>Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Address Name */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Address Name'
                  value={addressName}
                  onChange={(e) => setAddressName(e.target.value)}
                  error={!!errors.addressName}
                  helperText={errors.addressName}
                  placeholder='e.g., Home, Work, Apartment'
                />
              </Grid>

              {/* Building Number */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Building Number'
                  value={buildingNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 5) {
                      setBuildingNumber(value);
                    }
                  }}
                  error={!!errors.buildingNumber}
                  helperText={errors.buildingNumber}
                  inputProps={{ maxLength: 5 }}
                />
              </Grid>

              {/* Zone */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Zone'
                  value={zone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 5) {
                      setZone(value);
                    }
                  }}
                  error={!!errors.zone}
                  helperText={errors.zone}
                  inputProps={{ maxLength: 5 }}
                />
              </Grid>

              {/* Street */}
              <Grid item xs={12} sx={{ position: 'relative', zIndex: 2 }}>
                <TextField
                  fullWidth
                  name='street'
                  label='Street'
                  value={street}
                  onChange={(e) => {
                    setStreet(e.target.value);
                    if (errors.street) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.street;
                        return newErrors;
                      });
                    }
                  }}
                  error={!!errors.street}
                  helperText={errors.street}
                  placeholder='Enter street name'
                  inputProps={{ maxLength: 200 }}
                />
              </Grid>

              {/* Google Maps URL Input */}
              <Grid item xs={12}>
                <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                  <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 'bold' }}>
                    Extract from Google Maps URL
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      fullWidth
                      label='Google Maps URL'
                      placeholder='Paste Google Maps URL here'
                      value={googleMapsUrl}
                      onChange={handleUrlChange}
                      error={!!urlError}
                      helperText={urlError || 'Supports full URLs and shortened URLs'}
                      size='small'
                      disabled={isExtracting || isLoading}
                    />
                    <Button
                      variant='contained'
                      onClick={handleExtractFromUrl}
                      disabled={!googleMapsUrl.trim() || isExtracting || isLoading}
                      sx={{
                        minWidth: 120,
                        backgroundColor: '#EE3750 !important',
                        color: '#fff !important',
                        '&:hover': {
                          backgroundColor: '#EE3750 !important',
                        },
                        '&:disabled': {
                          backgroundColor: '#c4cdd5',
                          color: '#fff',
                        },
                      }}
                    >
                      {isExtracting ? (
                        <>
                          <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                          Extracting...
                        </>
                      ) : (
                        'Extract'
                      )}
                    </Button>
                  </Box>
                </Box>
              </Grid>

              {/* Interactive Map */}
              <Grid item xs={12}>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  Select Location on Map (Click on map to set coordinates)
                </Typography>
                <AddressLocationMap
                  latitude={typeof latitude === 'number' ? latitude : 25.3548}
                  longitude={typeof longitude === 'number' ? longitude : 51.1839}
                  onLocationSelect={handleMapClick}
                />
              </Grid>

              {/* Latitude and Longitude Input Fields */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label='Latitude'
                  type='number'
                  value={latitude !== null ? latitude : ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : parseFloat(e.target.value);
                    setLatitude(value);
                    if (errors.latitude) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.latitude;
                        return newErrors;
                      });
                    }
                  }}
                  error={!!errors.latitude}
                  helperText={errors.latitude || 'Range: -90 to 90'}
                  inputProps={{
                    step: 'any',
                    min: -90,
                    max: 90,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label='Longitude'
                  type='number'
                  value={longitude !== null ? longitude : ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : parseFloat(e.target.value);
                    setLongitude(value);
                    if (errors.longitude) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.longitude;
                        return newErrors;
                      });
                    }
                  }}
                  error={!!errors.longitude}
                  helperText={errors.longitude || 'Range: -180 to 180'}
                  inputProps={{
                    step: 'any',
                    min: -180,
                    max: 180,
                  }}
                />
              </Grid>

              {/* Location Preview */}
              {latitude !== null && longitude !== null && 
               typeof latitude === 'number' && typeof longitude === 'number' && (
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant='body2' color='text.secondary'>
                      <strong>Selected Location:</strong> {latitude.toFixed(6)}, {longitude.toFixed(6)}
                    </Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                      <a 
                        href={`https://www.google.com/maps?q=${latitude},${longitude}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{ color: '#EE3750' }}
                      >
                        View on Google Maps
                      </a>
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Phone auto-filled from selected customer (required by address API) */}
              <Grid item xs={12}>
                <Typography variant='body2' color={errors.phoneNumber ? 'error' : 'text.secondary'}>
                  <strong>Phone (from customer):</strong>{' '}
                  {customerPhoneNumber || 'No phone on selected customer'}
                </Typography>
                {errors.phoneNumber && (
                  <FormHelperText error>{errors.phoneNumber}</FormHelperText>
                )}
              </Grid>

              {/* Is Default */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isDefault}
                      onChange={(e) => setIsDefault(e.target.checked)}
                      sx={{
                        color: '#EE3750',
                        '&.Mui-checked': {
                          color: '#EE3750',
                        },
                      }}
                    />
                  }
                  label='Set as default address'
                />
              </Grid>

              {/* Error Message */}
              {errors.submit && (
                <Grid item xs={12}>
                  <FormHelperText error>{errors.submit}</FormHelperText>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading || isExtracting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={isLoading || isExtracting || isFetchingDefaults}
          sx={{
            backgroundColor: '#EE3750 !important',
            color: '#fff !important',
            '&:hover': {
              backgroundColor: '#EE3750 !important',
            },
            '&:disabled': {
              backgroundColor: '#c4cdd5',
              color: '#fff',
            },
          }}
        >
          {isLoading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
              Creating...
            </>
          ) : (
            'Create Address'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddressCreateDialog;
